import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiPackage, FiClock, FiCheckCircle, FiXCircle, 
  FiTruck, FiEye, FiChevronDown, FiChevronUp,
  FiSearch, FiCalendar, FiArrowLeft
} from 'react-icons/fi';
import api from '../services/api';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [continueShoppingLink, setContinueShoppingLink] = useState('/category/new-in');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get('/orders/my-orders');
        const ordersData = Array.isArray(res) ? res : (res.orders || []);
        setOrders(ordersData);
      } catch (err) {
        console.error('Failed to fetch orders:', err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.continueShoppingLink) {
          setContinueShoppingLink(res.continueShoppingLink);
        }
      } catch (err) {
        console.error('Failed to fetch continue shopping link', err);
      }
    };
    fetchLink();
  }, []);

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.orderStatus === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-[#22c55e] text-white';
      case 'shipped': return 'bg-[#3b82f6] text-white';
      case 'out_for_delivery': return 'bg-[#8b5cf6] text-white';
      case 'processing': return 'bg-[#f59e0b] text-white';
      case 'confirmed': return 'bg-[#06b6d4] text-white';
      case 'cancelled': return 'bg-[#ef4444] text-white';
      case 'pending': return 'bg-[#9ca3af] text-white';
      default: return 'bg-[#9ca3af] text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <FiCheckCircle />;
      case 'shipped':
      case 'out_for_delivery': return <FiTruck />;
      case 'processing':
      case 'confirmed':
      case 'pending': return <FiClock />;
      case 'cancelled': return <FiXCircle />;
      default: return <FiPackage />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'delivered': return 'Delivered';
      case 'shipped': return 'Shipped';
      case 'out_for_delivery': return 'Out for Delivery';
      case 'processing': return 'Processing';
      case 'confirmed': return 'Confirmed';
      case 'cancelled': return 'Cancelled';
      case 'pending': return 'Pending';
      default: return status ? status.replace(/_/g, ' ') : 'Processing';
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleTrackClick = (order) => {
    const email = order.shippingAddress?.email || order.user?.email || '';
    if (order.orderNumber) {
      navigate(`/track-order?orderNumber=${order.orderNumber}&email=${encodeURIComponent(email)}`);
    } else {
      alert('Tracking information is not available for this order yet.');
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  if (loading) return <div className="text-center py-10 text-gray-500">Loading orders...</div>;

  return (
    <div className="min-h-screen bg-[#f8f8f8] py-8 px-4 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-gray-900 tracking-wide mb-2">My Orders</h1>
              <p className="text-gray-500 text-sm">
                Manage and track your orders here. {filteredOrders.length} orders found.
              </p>
            </div>
            <Link 
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <FiArrowLeft size={16} />
              Back to Dashboard
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <FiSearch className="text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Search orders..." 
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d60167]"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500">Filter:</span>
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d60167] bg-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <FiPackage className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500 mb-4">You haven't placed any orders matching this filter yet.</p>
            <Link to={continueShoppingLink} className="inline-flex items-center px-6 py-3 bg-[#d60167] text-white rounded-lg hover:bg-[#b0005a] transition-colors">
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => (
              <div key={order._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                        {order.items && order.items.length > 0 && order.items[0].productImage ? (
                          <img src={order.items[0].productImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-200" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-medium text-gray-900">{order.orderNumber || order._id}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.orderStatus)} flex items-center gap-1`}>
                            {getStatusIcon(order.orderStatus)}
                            {getStatusLabel(order.orderStatus)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                        </p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                          <span>Rs. {order.total.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => handleTrackClick(order)}
                        className="text-sm text-[#d60167] hover:text-[#b0005a] font-medium"
                      >
                        Track
                      </button>
                      <button onClick={() => toggleExpand(order._id)} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                        Details
                        {expandedOrder === order._id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                      </button>
                    </div>
                  </div>

                  {expandedOrder === order._id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Order Items</h4>
                          <div className="space-y-3">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <img 
                                  src={item.productImage || 'https://via.placeholder.com/50'} 
                                  alt={item.productName} 
                                  className="w-12 h-12 object-cover rounded border" 
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                                  <p className="text-xs text-gray-500">
                                    Qty: {item.quantity} 
                                    {item.color && <span className="ml-2">Color: <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span> {item.color}</span>}
                                    {item.size && <span className="ml-2">Size: {item.size}</span>}
                                  </p>
                                </div>
                                <span className="text-sm text-gray-900">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Shipping Address</h4>
                          <p className="text-sm text-gray-600">
                            {order.shippingAddress?.name}<br />
                            {order.shippingAddress?.address}<br />
                            {order.shippingAddress?.city}, {order.shippingAddress?.province}<br />
                            Phone: {order.shippingAddress?.phone}
                          </p>
                          <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">Payment Method</h4>
                          <p className="text-sm text-gray-600 capitalize">{order.paymentMethod}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;